import { useEffect, useRef, useState, useCallback } from "react";
import { useTradePrice } from "../hooks/useTradePrice";
import { InfoClient, fromSatoshi } from "../sdk";
import {
  formatPriceFlexible as formatPrice,
  formatQuantity,
} from "../sdk/utils/display";
import {
  LoadingMessage,
  ErrorMessage,
  EmptyMessage,
  StatusIndicator,
  LayoutHeader,
} from "./smartlayout/fragments";
import {
  DEFAULT_BASE_URL,
  DEFAULT_WS_URL,
  DEFAULT_MARKET_ID,
  DEFAULT_LIMIT,
} from "./smartlayout/constants";
import "./smartlayout/layout.css";

interface TradePriceProps {
  baseURL?: string;
  wsURL?: string;
  marketId?: string;
  symbol?: string; // Symbol for mark_price subscription (e.g., "BTC/USDC")
  limit?: number;
  authToken?: string;
  walletAddress?: string;
  deviceID?: string;
}

function TradePrice({
  baseURL = DEFAULT_BASE_URL,
  wsURL = DEFAULT_WS_URL,
  marketId = DEFAULT_MARKET_ID,
  symbol,
  limit = DEFAULT_LIMIT,
  authToken,
  walletAddress,
  deviceID,
}: TradePriceProps): JSX.Element {
  const {
    lastPrice,
    markPrice,
    displayPrice,
    displayTimestamp,
    loading,
    error,
    isConnected,
    priceChange,
  } = useTradePrice({
    baseURL,
    wsURL,
    marketId,
    symbol, // Pass symbol to enable mark_price subscription
    limit,
    authToken,
    walletAddress,
    deviceID,
  });

  // State for recent trades table
  const [recentTrades, setRecentTrades] = useState<
    Array<{
      time: number;
      size: number;
      price: number;
      is_buyer_maker: boolean;
    }>
  >([]);
  const [loadingTrades, setLoadingTrades] = useState(false);
  const infoClientRef = useRef<InfoClient | null>(null);
  const isFetchingTradesRef = useRef(false); // Track if fetch is in progress to prevent overlapping requests
  const priceValueRef = useRef<HTMLDivElement | null>(null); // Ref for auto-sizing font

  // Fetch recent trades for the table
  const fetchRecentTrades = useCallback(async () => {
    if (!infoClientRef.current || !marketId) {
      console.debug("[TradePrice] fetchRecentTrades skipped: missing client or marketId");
      return;
    }

    // Prevent concurrent fetches
    if (isFetchingTradesRef.current) {
      console.debug("[TradePrice] fetchRecentTrades skipped: already fetching");
      return; // Already fetching, skip this call
    }

    console.debug("[TradePrice] Starting fetchRecentTrades");
    isFetchingTradesRef.current = true;
    setLoadingTrades(true);

    try {
      const trades = await infoClientRef.current.getRecentTrades(marketId, 20); // Fetch last 20 trades for table

      if (trades && Array.isArray(trades) && trades.length > 0) {
        // Helper to normalize timestamp (string or number) to milliseconds
        const normalizeTimestamp = (ts: string | number | undefined | null): number => {
          if (!ts) return Date.now();
          const num = typeof ts === "string" ? Number(ts) : ts;
          if (isNaN(num) || num <= 0) return Date.now();
          // If timestamp is in seconds (less than year 2000 in milliseconds), convert to milliseconds
          if (num < 946684800000) return num * 1000;
          return num;
        };

        // Sort by timestamp descending (newest first)
        const sortedTrades = [...trades].sort((a, b) => {
          const timeA = normalizeTimestamp(a.timestamp || a.time);
          const timeB = normalizeTimestamp(b.timestamp || b.time);
          return timeB - timeA; // Descending: newest first
        });

        // Convert to display format
        const formattedTrades = sortedTrades.map((trade) => {
          // Handle timestamp - convert string to number and ensure milliseconds
          const timestamp = normalizeTimestamp(trade.timestamp || trade.time);

          return {
            time: timestamp,
            size:
              typeof trade.quantity === "string"
                ? fromSatoshi(trade.quantity)
                : typeof trade.size === "string"
                  ? fromSatoshi(trade.size)
                  : trade.quantity || trade.size || 0,
            price:
              typeof trade.price === "string"
                ? fromSatoshi(trade.price)
                : trade.price || 0,
            is_buyer_maker: trade.is_buyer_maker !== undefined 
              ? trade.is_buyer_maker 
              : trade.side === "sell" 
                ? false 
                : true, // Default to buy if side is "buy" or undefined
          };
        });

        setRecentTrades(formattedTrades);
        console.debug("[TradePrice] fetchRecentTrades completed successfully");
      } else {
        // Clear trades if no data
        setRecentTrades([]);
        console.debug("[TradePrice] fetchRecentTrades: no trades returned");
      }
    } catch (err) {
      console.error("[TradePrice] Failed to fetch recent trades:", err);
      setRecentTrades([]); // Clear on error
    } finally {
      console.debug("[TradePrice] fetchRecentTrades finished, clearing loading state");
      setLoadingTrades(false);
      isFetchingTradesRef.current = false;
    }
  }, [marketId]);

  // Initialize InfoClient and fetch trades (only once, no polling)
  useEffect(() => {
    if (!marketId) {
      console.debug("[TradePrice] Initialization skipped: no marketId");
      return;
    }

    console.debug("[TradePrice] Initializing trades fetch for marketId:", marketId);

    // Reset fetching flag when marketId changes
    isFetchingTradesRef.current = false;
    setLoadingTrades(false);

    // Create new InfoClient instance
    infoClientRef.current = new InfoClient({ baseURL });
    
    // Fetch once with a small delay to ensure component is mounted
    const initialTimeout = setTimeout(() => {
      if (infoClientRef.current && marketId && !isFetchingTradesRef.current) {
        console.debug("[TradePrice] Executing initial fetchRecentTrades");
        fetchRecentTrades();
      } else {
        console.debug("[TradePrice] Initial fetch skipped:", {
          hasClient: !!infoClientRef.current,
          hasMarketId: !!marketId,
          isFetching: isFetchingTradesRef.current,
        });
      }
    }, 100);

    return () => {
      console.debug("[TradePrice] Cleaning up trades fetch effect");
      clearTimeout(initialTimeout);
      // Reset flag on cleanup
      isFetchingTradesRef.current = false;
      setLoadingTrades(false);
    };
    // Remove fetchRecentTrades from dependencies to prevent infinite loop
    // fetchRecentTrades uses marketId via closure, so we only need marketId and baseURL
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseURL, marketId]);

  // Auto-fit font size to container
  // Track previous price text to only adjust when text actually changes
  const prevPriceTextRef = useRef<string>("");
  const adjustFontSizeTimeoutRef = useRef<number | null>(null);

  // Shared function to calculate and apply font size
  const calculateFontSize = useCallback(() => {
    const element = priceValueRef.current;
    if (!element) return;

    const container = element.parentElement;
    if (!container) return;

    const text = element.textContent || "";
    if (!text) return;

    // Get container width (accounting for padding)
    const containerStyle = window.getComputedStyle(container);
    const containerPadding =
      parseFloat(containerStyle.paddingLeft) +
      parseFloat(containerStyle.paddingRight);
    const maxWidth = container.clientWidth - containerPadding;

    // Binary search for optimal font size (with min/max constraints)
    let minSize = 12; // Minimum readable font size
    let maxSize = 48; // Maximum font size to prevent it from being too large
    let optimalSize = minSize;

    // Create a temporary element to measure text width at different font sizes
    const measureElement = document.createElement("div");
    measureElement.style.position = "absolute";
    measureElement.style.visibility = "hidden";
    measureElement.style.whiteSpace = "nowrap";
    measureElement.style.fontFamily = window.getComputedStyle(element).fontFamily;
    measureElement.style.fontWeight = window.getComputedStyle(element).fontWeight;
    document.body.appendChild(measureElement);

    // Binary search for the largest font size that fits
    while (minSize <= maxSize) {
      const testSize = Math.floor((minSize + maxSize) / 2);
      measureElement.style.fontSize = `${testSize}px`;
      measureElement.textContent = text;
      const textWidth = measureElement.offsetWidth;

      if (textWidth <= maxWidth) {
        optimalSize = testSize;
        minSize = testSize + 1;
      } else {
        maxSize = testSize - 1;
      }
    }

    document.body.removeChild(measureElement);

    // Apply the optimal font size (only if it changed significantly to avoid unnecessary re-renders)
    const currentSize = parseFloat(window.getComputedStyle(element).fontSize);
    if (Math.abs(currentSize - optimalSize) > 0.5) {
      element.style.fontSize = `${optimalSize}px`;
    }
  }, []);

  useEffect(() => {
    if (displayPrice === null || displayPrice === undefined) return;

    const currentPriceText = formatPrice(displayPrice);
    
    // Only adjust font size if the price text actually changed
    if (currentPriceText === prevPriceTextRef.current) {
      return;
    }
    
    prevPriceTextRef.current = currentPriceText;

    // Clear any pending adjustment
    if (adjustFontSizeTimeoutRef.current !== null) {
      clearTimeout(adjustFontSizeTimeoutRef.current);
    }

    // Debounce font size adjustment to avoid excessive calculations
    adjustFontSizeTimeoutRef.current = window.setTimeout(() => {
      calculateFontSize();
    }, 150); // Debounce font size adjustments

    // Also adjust on window resize (with debounce for performance)
    let resizeTimeout: number;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = window.setTimeout(() => {
        if (adjustFontSizeTimeoutRef.current !== null) {
          clearTimeout(adjustFontSizeTimeoutRef.current);
        }
        adjustFontSizeTimeoutRef.current = window.setTimeout(() => {
          calculateFontSize();
        }, 150);
      }, 100); // Debounce resize events
    };

    window.addEventListener("resize", handleResize);
    return () => {
      if (adjustFontSizeTimeoutRef.current !== null) {
        clearTimeout(adjustFontSizeTimeoutRef.current);
      }
      clearTimeout(resizeTimeout);
      window.removeEventListener("resize", handleResize);
    };
  }, [displayPrice, calculateFontSize]);

  const formatTime = (timestamp: number): string => {
    // Validate timestamp
    if (!timestamp || isNaN(timestamp) || timestamp <= 0) {
      return "Invalid";
    }

    // Ensure timestamp is in milliseconds
    let ts = timestamp;
    if (ts < 946684800000) {
      // Less than Jan 1, 2000 in milliseconds, assume it's in seconds
      ts = ts * 1000;
    }

    const date = new Date(ts);
    
    // Validate date
    if (isNaN(date.getTime())) {
      return "Invalid";
    }

    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  const getSideColor = (isBuyerMaker: boolean): string => {
    return isBuyerMaker ? "#51cf66" : "#ff6b6b";
  };

  const getSideLabel = (isBuyerMaker: boolean): string => {
    return isBuyerMaker ? "BUY" : "SELL";
  };

  const getSideEmoji = (isBuyerMaker: boolean): string => {
    return isBuyerMaker ? "🟢" : "🔴";
  };

  if (loading) {
    return <LoadingMessage message="Loading trade price..." />;
  }

  if (error) {
    return <ErrorMessage error={error} />;
  }

  // If no display price available (neither lastPrice nor markPrice), show empty message
  if (displayPrice === null || displayPrice === undefined) {
    return <EmptyMessage message="No trade data available" />;
  }

  // Use lastPrice for side indicator if available, otherwise default to neutral
  const hasLastPrice = lastPrice && lastPrice.is_buyer_maker !== undefined;
  const sideColor = hasLastPrice
    ? getSideColor(lastPrice.is_buyer_maker)
    : "#888";
  const sideLabel = hasLastPrice
    ? getSideLabel(lastPrice.is_buyer_maker)
    : "N/A";
  const sideEmoji = hasLastPrice
    ? getSideEmoji(lastPrice.is_buyer_maker)
    : "⚪";
  const flashClass =
    priceChange === "up"
      ? "flash-price-up"
      : priceChange === "down"
        ? "flash-price-down"
        : "";

  return (
    <div className="layout-container trade-price-container">
      {/* Header */}
      <LayoutHeader title={<StatusIndicator isConnected={isConnected} />}>
        <div>Last Trade Price</div>
      </LayoutHeader>

      {/* Main Price Display */}
      <div className={`trade-price-main ${flashClass}`}>
        <div
          ref={priceValueRef}
          className="trade-price-value"
          style={{ color: sideColor }}
        >
          {formatPrice(displayPrice)}
        </div>
        <div className="trade-price-side" style={{ color: sideColor }}>
          <span>{sideEmoji}</span>
          <span>{sideLabel}</span>
        </div>
      </div>

      {/* Recent Trades Table */}
      <div className="trade-price-trades-table">
        {loadingTrades ? (
          <div
            style={{
              padding: "1rem",
              textAlign: "center",
              color: "#888",
            }}
          >
            Loading trades...
          </div>
        ) : recentTrades.length === 0 ? (
          <div
            style={{
              padding: "1rem",
              textAlign: "center",
              color: "#888",
            }}
          >
            No trades available
          </div>
        ) : (
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "0.85rem",
            }}
          >
            <thead>
              <tr
                style={{
                  borderBottom: "1px solid #2a2e39",
                  color: "#888",
                }}
              >
                <th
                  style={{
                    textAlign: "left",
                    padding: "0.5rem",
                    fontWeight: "500",
                  }}
                >
                  Time
                </th>
                <th
                  style={{
                    textAlign: "right",
                    padding: "0.5rem",
                    fontWeight: "500",
                  }}
                >
                  Size
                </th>
                <th
                  style={{
                    textAlign: "right",
                    padding: "0.5rem",
                    fontWeight: "500",
                  }}
                >
                  Price
                </th>
              </tr>
            </thead>
            <tbody>
              {recentTrades.map((trade, index) => {
                const tradeColor = trade.is_buyer_maker ? "#51cf66" : "#ff6b6b";
                return (
                  <tr
                    key={index}
                    style={{
                      borderBottom: "1px solid #2a2e39",
                    }}
                  >
                    <td
                      style={{
                        padding: "0.5rem",
                        color: "#d1d4dc",
                      }}
                    >
                      {formatTime(trade.time)}
                    </td>
                    <td
                      style={{
                        padding: "0.5rem",
                        textAlign: "right",
                        color: "#d1d4dc",
                      }}
                    >
                      {formatQuantity(trade.size)}
                    </td>
                    <td
                      style={{
                        padding: "0.5rem",
                        textAlign: "right",
                        color: tradeColor,
                        fontWeight: "500",
                      }}
                    >
                      {formatPrice(trade.price)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default TradePrice;
